import React, { useCallback, useEffect, useState, useRef } from 'react';
import './DataPropertiesPanel.less';
import { getPropertiesSnapshot } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData/DataDesignerService';
import { DataDesignerSurfaceType } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import { PropertyGrid } from 'components/property-editors/PropertyGrid';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import classNames from 'classnames';
import { DataDesignerView } from './DataDesignerReducer';
import { IAddBeforeSavePromises } from 'hooks/useBeforeSavePromises';
import { isAbortError } from 'utils/ErrorUtils';

interface IInputDataPropertiesPanelProps extends React.HTMLProps<HTMLDivElement>, IAddBeforeSavePromises {
  isVisible: boolean;
  sessionId: string;
  diagramId: string;
  surfaceType: DataDesignerSurfaceType;
  currentView: DataDesignerView;
}

/**
 * Shows DataDiagram properties if inputDataId is provided and design pattern properties otherwise.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export const DiagramPropertiesPanel = React.memo(
  ({
    sessionId,
    diagramId,
    surfaceType,
    currentView,
    className,
    isVisible,
    addBeforeSavePromise,
    removeBeforeSavePromise,
  }: IInputDataPropertiesPanelProps) => {
    const [snapshot, setSnapshot] = useState<ContextProviderNode | undefined>(undefined);
    const abort = useRef<AbortController | null>(null);

    const refreshSnapshot = useCallback(async () => {
      abort.current && abort.current.abort();
      abort.current = new AbortController();

      try {
        const pgSnapshot = await getPropertiesSnapshot(
          { sessionId, diagramId, surfaceType, isListView: currentView === 'List View' },
          abort.current
        );
        setSnapshot(pgSnapshot);
      } catch (err) {
        if (!isAbortError(err)) {
          throw err;
        }
      }
    }, [currentView, diagramId, sessionId, surfaceType]);

    useEffect(() => {
      if (isVisible) {
        refreshSnapshot();
      }
    }, [isVisible, refreshSnapshot]);

    return (
      <div className={classNames('dp-data-properties-panel', className)}>
        {!!snapshot && (
          <PropertyGrid
            snapshot={snapshot}
            addBeforeSavePromise={addBeforeSavePromise}
            removeBeforeSavePromise={removeBeforeSavePromise}
          />
        )}
      </div>
    );
  }
);
